'use client'

import { BranchSelect } from '@repo/ui/BranchSelect'
import { CrossIcon } from '@repo/ui/CrossIcon'
import { DepartmentSelect } from '@repo/ui/DepartmentSelect'
import { Divider } from '@mui/material'
import { FeeEarnerSelect } from '@repo/ui/FeeEarnerSelect'
import { LargeCashIcon } from '@repo/ui/LargeCashIcon'
import { LoadingState } from '@repo/ui/LoadingState'
import { TextLinkButton } from '@repo/ui/TextLinkButton'
import { ThirdPartyCaseTypeSelect } from '@repo/ui/ThirdPartyCaseTypeSelect'
import { ThirdPartyNotificationRecipientSelect } from '@repo/ui/ThirdPartyNotificationRecipientSelect'
import { useNavigate } from 'react-router-dom'
import { useRef, useState, useEffect } from 'react'
import { useTheme } from '@mui/material/styles'
import { useThirdPartyCaseTypesStore } from '@repo/store/useThirdPartyCaseTypesStore'
import { WizardFooter } from '@repo/ui/WizardFooter'
import { WizardFormSection } from '@repo/ui/WizardFormSection'
import { WizardIconTitle } from '@repo/ui/WizardIconTitle'
import { WizardPanel } from '@repo/ui/WizardPanel'
import { WorktypeSelect } from '@repo/ui/WorktypeSelect'
import MUIButton from '@mui/material/Button'
import MUIStack from '@mui/material/Stack'
import MUITextField from '@mui/material/TextField'
import MUITypography from '@mui/material/Typography'
import PropTypes from 'prop-types'

export const NewPerfectPortalCaseTypeMapping = ({ wideMode, handlePrevNext, updateStepState, testMode = false }) => {
  const { createNewMatterDefault, setNewMatterDefaultData, newMatterDefaultsData, updateNewMatterDefaults } =
    useThirdPartyCaseTypesStore()

  const formRef = useRef()
  const navigate = useNavigate()
  const theme = useTheme()

  const [branchId, setBranchId] = useState(null)
  const [departmentId, setDepartmentId] = useState(null)
  const [feeEarner, setFeeEarner] = useState(null)
  const [isEditCaseTypeMapping, setIsEditCaseTypeMapping] = useState(false)
  const [loading, setLoading] = useState(false)
  const [matterDescription, setMatterDescription] = useState(null)
  const [newMatterDefaultId, setNewMatterDefaultId] = useState(null)
  const [notificationRecipients, setNotificationRecipients] = useState([{}])
  const [notificationsOptions, setNotificationsOptions] = useState([])
  const [selectedNotification, setSelectedNotification] = useState(null)
  const [sourceWorkTypeId, setSourceWorkTypeId] = useState(null)
  const [sourceWorkTypeName, setSourceWorkTypeName] = useState(null)
  const [supervisingFeeEarner, setSupervisingFeeEarner] = useState(null)
  const [workTypeId, setWorkTypeId] = useState(null)

  useEffect(() => {
    const urlObj = new URL(window.location.href)
    const hash = urlObj.hash
    const searchParams = new URLSearchParams(hash.slice(hash.indexOf('?') + 1))
    setIsEditCaseTypeMapping(searchParams.get('editCaseTypeMapping') === 'true')
  }, [])

  useEffect(() => {
    if (!isEditCaseTypeMapping) return

    setLoading(true)
    setBranchId(newMatterDefaultsData.branchId)
    setDepartmentId(newMatterDefaultsData.departmentId)
    setFeeEarner(newMatterDefaultsData.feeEarnerId)
    setMatterDescription(newMatterDefaultsData.matterDescription)
    setNewMatterDefaultId(newMatterDefaultsData.newMatterDefaultId)
    setSourceWorkTypeId(newMatterDefaultsData.sourceWorkTypeId)
    setSourceWorkTypeName(newMatterDefaultsData.sourceWorkTypeName)
    setSupervisingFeeEarner(newMatterDefaultsData.supervisingFeeEarnerId)
    setWorkTypeId(newMatterDefaultsData.workTypeId)
    setSelectedNotification({
      label: `${newMatterDefaultsData.notificationRecipientUserId} - ${newMatterDefaultsData.notificationRecipientEmailAddress}`,
      emailAddress: newMatterDefaultsData.notificationRecipientEmailAddress,
      value: newMatterDefaultsData.notificationRecipientUserId
    })
    setNotificationRecipients(newMatterDefaultsData.notificationRecipients)

    setTimeout(() => {
      setLoading(false)
    }, 6000)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditCaseTypeMapping])

  useEffect(() => {
    !isEditCaseTypeMapping &&
      setNewMatterDefaultData({
        newMatterDefaultsData: {
          branchId: branchId ? branchId.branchId : null,
          departmentId: departmentId ? departmentId.departmentId : null,
          feeEarnerId: feeEarner ? feeEarner.feeEarnerId : null,
          matterDescription: matterDescription ?? null,
          newMatterDefaultId: newMatterDefaultId ?? null,
          notificationRecipients: notificationRecipients ?? null,
          notificationSourceId: 'PERFECTPORTAL',
          sourceWorkTypeId: sourceWorkTypeId ?? null,
          sourceWorkTypeName: sourceWorkTypeName ?? null,
          supervisingFeeEarnerId: supervisingFeeEarner ? supervisingFeeEarner.feeEarnerId : null,
          workTypeId: workTypeId ? workTypeId.workflowTypeId : null
        }
      })
  }, [
    branchId,
    departmentId,
    feeEarner,
    isEditCaseTypeMapping,
    matterDescription,
    newMatterDefaultId,
    notificationRecipients,
    selectedNotification,
    setNewMatterDefaultData,
    sourceWorkTypeName,
    sourceWorkTypeId,
    supervisingFeeEarner,
    workTypeId
  ])

  const handleClickBack = () => {
    handlePrevNext(-1)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!isEditCaseTypeMapping) {
      await createNewMatterDefault()
      await updateStepState({ status: 'completed', statusText: 'Complete' })
      navigate(`/supervisor/integrations/perfectportal-defaults?formComplete=true`)
    } else {
      await setNewMatterDefaultData({
        newMatterDefaultsData: {
          branchId: branchId,
          departmentId: departmentId,
          feeEarnerId: feeEarner,
          matterDescription: matterDescription,
          newMatterDefaultId: newMatterDefaultId,
          notificationRecipients: notificationRecipients,
          notificationSourceId: 'PERFECTPORTAL',
          sourceWorkTypeId: sourceWorkTypeId,
          sourceWorkTypeName: sourceWorkTypeName,
          supervisingFeeEarnerId: supervisingFeeEarner,
          workTypeId: workTypeId
        }
      })

      await updateNewMatterDefaults()
      navigate(`/supervisor/integrations/perfectportal-defaults?formComplete=true`)
    }
  }

  const addNotificationRecipient = (value) => {
    setNotificationRecipients([...notificationRecipients, { userId: value.value, emailAddress: value.emailAddress }])
  }

  const removeNotificationRecipient = (index) => {
    setNotificationRecipients(notificationRecipients.filter((_, i) => i !== index))
  }

  return (
    <>
      <WizardPanel ref={formRef} data-testid="NewPerfectPortalCaseTypeMapping">
        <WizardFormSection>
          <WizardIconTitle
            title={isEditCaseTypeMapping ? 'Edit case type mapping' : 'Add new case type mapping'}
            icon={LargeCashIcon}
            sx={{
              alignItems: 'flex-start',
              justifyContent: 'flex-end',
              [`& .MuiStack-root`]: {
                alignItems: 'flex-end'
              }
            }}
          />
          <Divider sx={{ borderTop: 'solid 1px rgba(5, 28, 51, 0.12)', mb: 2, width: '100%' }} />

          {loading && <LoadingState />}
          <MUIStack
            gap={4}
            direction="column"
            alignItems="flex-start"
            flexWrap={'wrap'}
            sx={{ visibility: loading ? 'hidden' : 'visible', width: '100%' }}
          >
            <MUIStack sx={{ width: '100%' }}>
              <MUITypography sx={{ mb: 4, display: loading ? 'none' : 'block' }}>
                Select the Perfect Portal case type:
              </MUITypography>
              <ThirdPartyCaseTypeSelect
                label="Perfect Portal case type"
                onChange={(value) => {
                  setSourceWorkTypeId(value?.value || '')
                  setSourceWorkTypeName(value?.label || '')
                }}
                size={'medium'}
                required={true}
                sx={{ width: 'calc(50% - 6px)' }}
                value={sourceWorkTypeId}
                isEditCaseTypeMapping={isEditCaseTypeMapping}
                testData={testMode ? [] : null}
              />
            </MUIStack>
            <MUIStack sx={{ width: '100%' }}>
              <MUITypography sx={{ mb: 4 }}>Followed by the mappings below:</MUITypography>
              <MUIStack
                display={'grid'}
                gap={'12px'}
                sx={{ gridTemplateColumns: 'repeat(2, 1fr)', gridTemplateRows: 'repeat(4, 1fr)', width: '100%' }}
              >
                <WorktypeSelect
                  name={`workTypeId`}
                  onChange={(value) => setWorkTypeId(value)}
                  required
                  multiple={false}
                  size={'medium'}
                  value={workTypeId}
                  testData={testMode ? [] : null}
                />
                <MUITextField
                  label={'Matter description'}
                  onChange={(e) => setMatterDescription(e.target.value)}
                  value={matterDescription}
                  InputLabelProps={{ shrink: matterDescription && matterDescription !== '' }}
                />
                <FeeEarnerSelect
                  name="feeEarner"
                  value={feeEarner}
                  onChange={setFeeEarner}
                  size={'medium'}
                  testData={testMode ? [] : null}
                />
                <FeeEarnerSelect
                  value={supervisingFeeEarner}
                  onChange={setSupervisingFeeEarner}
                  size={'medium'}
                  label="Supervising fee earner"
                  name="supervisingFeeEarner"
                  onlyIncludeSupervisingFeeEarners={true}
                  testData={testMode ? [] : null}
                />
                <BranchSelect
                  name={'branchId'}
                  onChange={(value) => {
                    setBranchId(value)
                  }}
                  size={'medium'}
                  value={branchId}
                  testData={testMode ? [] : null}
                />
                <DepartmentSelect
                  name={`departmentId`}
                  onChange={(value) => setDepartmentId(value)}
                  size={'medium'}
                  value={departmentId}
                  testData={testMode ? [] : null}
                />
                {notificationRecipients &&
                  notificationRecipients.map((rec, idx) => (
                    <MUIStack
                      key={idx}
                      direction="row"
                      alignItems="center"
                      spacing={2}
                      sx={{
                        position: 'relative',
                        gridArea: `${4 + idx} / 1 / ${5 + idx} / 3`,
                        width: '100%',
                        [`& .MuiInputBase-root`]: { mb: 0 }
                      }}
                    >
                      <ThirdPartyNotificationRecipientSelect
                        label="Notification recipient"
                        items={notificationsOptions}
                        setSelected={setSelectedNotification}
                        setOptions={setNotificationsOptions}
                        size="medium"
                        value={rec.userId || ''}
                        onChange={(value) => {
                          const updated = [...notificationRecipients]
                          updated[idx] = { userId: value.value, emailAddress: value.emailAddress }
                          setNotificationRecipients(updated)
                        }}
                        testData={testMode ? [] : null}
                        sx={{ flexGrow: 1 }}
                      />

                      <MUIButton
                        onClick={() => removeNotificationRecipient(idx)}
                        sx={{
                          width: '24px',
                          position: 'absolute',
                          right: '-23px',
                          padding: 0,
                          margin: 0,
                          minWidth: '24px',
                          marginLeft: '0 !important'
                        }}
                      >
                        <CrossIcon
                          sx={{
                            width: '24px',
                            height: '24px',
                            color: theme?.palette?.secondary?.dark || 'black'
                          }}
                        />
                      </MUIButton>
                    </MUIStack>
                  ))}
                <TextLinkButton
                  onClick={addNotificationRecipient}
                  sx={{
                    mt: 1,
                    ml: 5,
                    width: '100%',
                    mb: 0,
                    gridArea: `${5 + notificationRecipients.length} / 1 / ${4 + notificationRecipients.length} / 3`
                  }}
                  title={
                    <MUIStack direction="row" alignItems="center" gap={2}>
                      <CrossIcon
                        sx={{
                          transform: 'rotate(45deg)',
                          width: '24px',
                          height: '24px',
                          color: theme?.palette?.secondary?.dark || 'black'
                        }}
                      />
                      Add additional notification recipient
                    </MUIStack>
                  }
                />
              </MUIStack>
            </MUIStack>
          </MUIStack>
        </WizardFormSection>
      </WizardPanel>
      <WizardFooter
        wideMode={wideMode}
        handleClickContinue={handleSubmit}
        handleClickBack={handleClickBack}
        continueButtonText="Save"
        backButtonVisible={false}
        continueButtonIsFormSubmitButton={true}
      />
    </>
  )
}

NewPerfectPortalCaseTypeMapping.propTypes = {
  wideMode: PropTypes.bool,
  handlePrevNext: PropTypes.func,
  updateStepState: PropTypes.func,
  testMode: PropTypes.bool
}
